import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const A4 = Object.freeze({ width: 595.28, height: 841.89, margin: 18 });
const OFFICIAL_PAGE_SELECTOR = ".page, .pacs-letterhead-page, [data-official-page]";

const officialPages = (documentRef) =>
  Array.from(documentRef?.querySelectorAll?.(OFFICIAL_PAGE_SELECTOR) || []);

const pdfFileName = (value) => {
  const name = String(value || "Official_Document.pdf").trim();
  return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
};

const waitForImages = async (documentRef) => {
  const images = Array.from(documentRef.images || []);
  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            image.removeEventListener("load", finish);
            image.removeEventListener("error", finish);
            resolve();
          };
          const timeout = window.setTimeout(finish, 15000);
          if (image.complete) return finish();
          image.addEventListener("load", finish, { once: true });
          image.addEventListener("error", finish, { once: true });
        }),
    ),
  );
};

const createRenderFrame = async (html) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("title", "PDF rendering workspace");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, {
    position: "fixed",
    left: "-12000px",
    top: "0",
    width: "220mm",
    height: "300mm",
    border: "0",
    background: "white",
    pointerEvents: "none",
  });

  const loaded = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error("PDF rendering workspace timed out.")),
      15000,
    );
    frame.addEventListener(
      "load",
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });
  /*
    IMPORTANT: assign srcdoc before attaching the iframe. If it is attached
    first, Chromium can fire the initial about:blank load event and resolve the
    promise before the official HTML is parsed. That race produced an empty
    document and the misleading "No official document pages" error.
  */
  frame.srcdoc = String(html || "");
  document.body.appendChild(frame);
  try {
    await loaded;
  } catch (error) {
    frame.remove();
    throw error;
  }

  const documentRef = frame.contentDocument;
  if (!documentRef) {
    frame.remove();
    throw new Error("PDF rendering workspace could not be created.");
  }

  /*
    Extra Chromium/Edge safety: if an extension or browser race still exposes
    the initial blank document, write the same HTML directly into the
    same-origin iframe and continue from that parsed document.
  */
  if (!officialPages(documentRef).length && String(html || "").trim()) {
    documentRef.open();
    documentRef.write(String(html));
    documentRef.close();
  }

  if (documentRef.fonts?.ready) await documentRef.fonts.ready;
  await waitForImages(documentRef);

  // Allow the letterhead auto-fit script and final image layout to settle.
  await new Promise((resolve) =>
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(resolve),
    ),
  );
  return { frame, documentRef };
};

const renderOfficialPdf = async (html, fileName) => {
  const { frame, documentRef } = await createRenderFrame(html);

  try {
    /*
      Legacy official forms use .page while PACS letterhead documents use
      .pacs-letterhead-page. Supporting both keeps every existing PDF workflow
      intact and fixes the "No official document pages" generation error.
    */
    const pages = officialPages(documentRef);
    if (!pages.length)
      throw new Error("No official document pages were found for PDF generation.");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    for (let index = 0; index < pages.length; index += 1) {
      const page = pages[index];
      const canvas = await html2canvas(page, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 20000,
        scrollX: 0,
        scrollY: 0,
        windowWidth: Math.max(documentRef.documentElement.scrollWidth, 900),
        windowHeight: Math.max(documentRef.documentElement.scrollHeight, 1200),
      });

      if (index > 0) pdf.addPage("a4", "portrait");

      const maxWidth = A4.width - A4.margin * 2;
      const maxHeight = A4.height - A4.margin * 2;
      const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
      const width = canvas.width * ratio;
      const height = canvas.height * ratio;
      const x = (A4.width - width) / 2;
      const y = (A4.height - height) / 2;

      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.96),
        "JPEG",
        x,
        y,
        width,
        height,
        undefined,
        "FAST",
      );

      canvas.width = 1;
      canvas.height = 1;
    }

    pdf.setProperties({ title: pdfFileName(fileName).replace(/\.pdf$/i, "") });
    return pdf;
  } finally {
    frame.remove();
  }
};

export async function officialHtmlToPdfBlob(html, fileName) {
  const pdf = await renderOfficialPdf(html, fileName);
  return pdf.output("blob");
}

export async function downloadOfficialPdf(html, fileName) {
  const pdf = await renderOfficialPdf(html, fileName);
  pdf.save(pdfFileName(fileName));
}

export function openOfficialPreview(html) {
  const previewWindow = window.open("", "_blank");
  if (!previewWindow) throw new Error("Please allow pop-ups for document preview.");
  previewWindow.opener = null;

  const previewCss = `<style>
    .official-preview-bar{position:sticky;top:0;z-index:9999;display:flex;justify-content:center;gap:10px;padding:10px;background:#062b58;box-shadow:0 4px 16px #0002}
    .official-preview-bar button{border:1px solid #ffffff55;border-radius:8px;padding:9px 15px;background:white;color:#062b58;font:700 13px Arial;cursor:pointer}
    @media print{.official-preview-bar{display:none!important}}
  </style>`;
  const toolbar = `<div class="official-preview-bar no-print" data-html2canvas-ignore="true"><button onclick="window.print()">Print / Save as PDF</button><button onclick="window.close()">Close Preview</button></div>`;
  const previewHtml = String(html)
    .replace("</head>", `${previewCss}</head>`)
    .replace(/<body([^>]*)>/i, `<body$1>${toolbar}`);

  previewWindow.document.open();
  previewWindow.document.write(previewHtml);
  previewWindow.document.close();
  previewWindow.focus();
}
